import React, { useContext, useMemo } from "react";
import {
  Box,
  Checkbox,
  CheckboxGroup,
  Column,
  Columns,
  Divider,
  Hidden,
  Stack,
  Text,
  TextLink,
} from "@origin-digital/ods-core";
import {
  formDataToFormikMapper,
  getErrorTypeEnum,
  premiseFormFieldMapper,
} from "src/api/apiHelper";
import { apiClient } from "src/api/apiService";
import {
  Field,
  FieldInputProps,
  FieldMetaProps,
  Form,
  useFormikContext,
  yupToFormErrors,
} from "formik";
import FormContext from "src/context/FormContext";

import FormFieldLayout from "./FormComponents/FormFieldLayout";
import { AccountPayer, FormValues, Service } from "./types";
import { useNavigate } from "react-router-dom";
import {
  formValidationSchemaInternet,
  homeUserFormValSchemaInternet,
} from "./FormComponents/Validations";
import UpdatePremiseAlert from "./UpdatePremiseAlert";
import {
  getNestedValue,
  isUnavailableDateForFuture,
  showUpdatePremiseAlert,
} from "./helper";
import { useUpdateSidebarStatus } from "../Hooks/useSidebarStatus";
import { useAppContext } from "src/context/AppContext";
import PermissionsActionButtons from "./PermissionsActionButtons";
import { InputValueType } from "@origin-digital/ods-types/.";
import { DatePicker, DatePickerProvider } from "@origin-digital/ods-pickers";
import { usePermissions } from "src/hooks/usePermissions";

const InternetBlock = (props: {
  setFormLoadValues: React.Dispatch<React.SetStateAction<FormValues>>;
  homeUser: boolean;
  premiseId: string | null;
}) => {
  const {
    context: { user },
  } = useAppContext();
  const navigate = useNavigate();
  const {
    formState: { internetService, isAccountReadOnly },
    setFormState,
    setDisplayingSnackbar,
    setDisplayingAlertBanner,
  } = useContext(FormContext);

  const { values, setFieldValue, setErrors, errors } =
    useFormikContext<FormValues>();
  const { setFormLoadValues, premiseId, homeUser } = props;
  const { isHomeUser: isHomeUserFromPermissions } = usePermissions();

  const isDigitalEic =
    homeUser ||
    (values.internetAccountPayer === AccountPayer.SOMEONE_ELSE &&
      values.accountHolderDetailsType === "online");

  const validationSchema = (readOnly?: boolean) =>
    homeUser
      ? homeUserFormValSchemaInternet(readOnly, isDigitalEic)
      : formValidationSchemaInternet(readOnly, isDigitalEic);

  const presubmissionRequestUpdate = async (isLodge: boolean) => {
    const manipulatedValues =
      isHomeUserFromPermissions &&
      values.accountHolder === "person" &&
      !accountPayers.includes(AccountPayer.SOMEONE_ELSE) &&
      user
        ? {
            ...values,
            accountPrimaryEmail: user.email,
            accountPrimaryFirstName: user.first_name,
            accountPrimaryLastName: user.last_name,
          }
        : values;
    try {
      const updatePremiseResponse = await apiClient.post(
        "/draft/premise",
        premiseFormFieldMapper(
          manipulatedValues,
          {
            internetService: internetService ?? {},
          },
          {},
          false,
          undefined,
          isDigitalEic && isLodge,
          homeUser
        )
      );

      const mappedFormikValues = formDataToFormikMapper(
        updatePremiseResponse.data
      );
      setFormLoadValues({
        ...mappedFormikValues,
        siteSupervisors: values.siteSupervisors,
      });

      const internetServiceResponse = updatePremiseResponse.data.services.find(
        (s: Service) => s.service_type === "INTERNET"
      );

      setFormState((prevState) => ({
        ...prevState,
        internetService: {
          draftId: internetServiceResponse?.draft_id,
          draftVersion: internetServiceResponse?.draft_version,
          requestId: internetServiceResponse?.request_id,
        },
      }));
      setDisplayingAlertBanner(null);
      navigate("/", {
        state: {
          submitted: true,
        },
      });
    } catch (e: any) {
      setDisplayingAlertBanner(getErrorTypeEnum(e) ?? null);
    }
  };

  const saveInternetBlock = async () => {
    try {
      await presubmissionRequestUpdate(false);
      setDisplayingSnackbar({
        message: "Internet block updated",
        tone: "positive",
      });
    } catch (error) {
      setDisplayingSnackbar({
        message: "Failed to save Internet block",
        tone: "caution",
      });
    }
  };

  const accountPayers = useMemo(
    () => [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ],
    [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ]
  );

  useUpdateSidebarStatus(
    accountPayers,
    Boolean(values.accountId),
    "internetBlock",
    Boolean(premiseId)
  );
  const value: string | undefined | null = getNestedValue(
    values,
    "internetHandoverDate"
  );
  const errorInDt: string | undefined = getNestedValue(
    errors,
    "internetHandoverDate"
  );
  const helperText = errorInDt?.toString();
  const handleOnChange = React.useCallback(
    (date: Date | null) => {
      if (date != value) {
        setFieldValue("internetHandoverDate", date);
      }
    },
    ["internetHandoverDate", setFieldValue, value]
  );
  const isLodgeInternet = Boolean(internetService?.handoverDate);
  const datePickerRef = React.useRef<HTMLDivElement>(null);

  return (
    <>
      {showUpdatePremiseAlert(
        accountPayers,
        Boolean(values.accountId),
        Boolean(premiseId)
      ) ? (
        <Stack space="xlarge">
          <UpdatePremiseAlert />
        </Stack>
      ) : (
        <Form>
          <Stack
            space={{
              xs: "xlarge",
              md: "xxlarge",
            }}
          >
            {values.locId ? (
              <Columns
                collapseBelow="md"
                space={{
                  xs: "none",
                  md: "xlarge",
                }}
                alignY="stretch"
              >
                <Column width="2/3">
                  {/* LOC ID */}
                  <Stack space="xsmall">
                    <Text variant="body-small" tone="neutralLight">
                      LOC ID
                    </Text>
                    <Text>{values.locId}</Text>
                    <Divider />
                  </Stack>
                </Column>
              </Columns>
            ) : null}
            <Columns
              collapseBelow="md"
              space={{
                xs: "none",
                md: "xlarge",
              }}
              alignY="stretch"
            >
              <Column width="2/3">
                <FormFieldLayout>
                  <div ref={datePickerRef}>
                    <DatePickerProvider>
                      <DatePicker
                        id={"internetHandoverDate"}
                        label={"Handover date"}
                        muiProps={{
                          slotProps: {
                            textField: {
                              variant: "outlined",
                            },
                            popper: {
                              disablePortal: true,
                              anchorEl: datePickerRef.current,
                            },
                          },
                        }}
                        onChange={handleOnChange}
                        value={
                          internetService?.handoverDate
                            ? new Date(internetService.handoverDate)
                            : value
                        }
                        shouldDisableDate={isUnavailableDateForFuture}
                        helperText={helperText}
                        error={!!helperText}
                      />
                    </DatePickerProvider>
                  </div>
                </FormFieldLayout>
                <Hidden below="lg">
                  <Column />
                </Hidden>
              </Column>
              <Column width="1/3">
                <Box display="flex" alignItems="stretch" height="full">
                  <Hidden below="md">
                    <Box
                      style={{ borderLeft: "1px solid #E3E3E3" }}
                      paddingRight="large"
                      height="full"
                    />
                  </Hidden>
                  <Box alignSelf="center" marginTop="xsmall">
                    <Stack>
                      <Text variant="body-small" tone="neutralLight">
                        Once the request is lodged, you can change the handover
                        date if it’s still more than 30 days away. Once it’s
                        less than 30 days away, the date is locked and can’t be
                        changed.
                      </Text>
                      {/* If more paragraphs of <Text> required, add here */}
                    </Stack>
                  </Box>
                </Box>
              </Column>
            </Columns>
            {values.internetAccountPayer === AccountPayer.SOMEONE_ELSE && (
              <Columns
                collapseBelow="md"
                space={{
                  xs: "none",
                  md: "xlarge",
                }}
                alignY="stretch"
              >
                {/* Fields */}
                <Column width="2/3">
                  <Field type="checkbox" name="internetTerms">
                    {({
                      field,
                      meta,
                    }: {
                      field: FieldInputProps<InputValueType>;
                      meta: FieldMetaProps<FormValues>;
                    }) => (
                      <CheckboxGroup
                        id="internetTermsGroup"
                        error={!!meta.error}
                        helperText={meta.error}
                      >
                        <Checkbox
                          {...field}
                          label={
                            <Text>
                              I have let the account holder know that their
                              information has been provided to Origin and told
                              them about Origin’s{" "}
                              <TextLink href="">
                                privacy and credit reporting statements
                              </TextLink>
                              .
                            </Text>
                          }
                          id="internetTerms"
                          checked={field.checked}
                          name="internetTerms"
                          onChange={(e) => {
                            setFieldValue("internetTerms", e.target.checked);
                          }}
                        />
                      </CheckboxGroup>
                    )}
                  </Field>
                </Column>
              </Columns>
            )}
            <PermissionsActionButtons
              primaryButtonProps={{
                onHandleClick: () =>
                  validationSchema(isAccountReadOnly)
                    .validate(values, {
                      abortEarly: false,
                    })
                    .then(async () => {
                      await presubmissionRequestUpdate(true);
                    })
                    .catch((e) => {
                      console.log("e", e);
                      setErrors(yupToFormErrors(e));
                    }),
              }}
              secondaryButtonProps={{
                onHandleClick: saveInternetBlock,
                disabled: internetService?.autoSubmit,
              }}
              isInternet={isLodgeInternet}
              showSecondaryButton={false}
            />
          </Stack>
        </Form>
      )}
    </>
  );
};

export default InternetBlock;
